import * as React from 'react';
import { Container, Grid2, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField as MuiTextField, Tooltip, Typography } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../setup/Navbar';
import AutoComplete from '../../../BuiltInComponent/AutoComplete';
import ButtonNormal from '../../../BuiltInComponent/ButtonNormal';
import DatePickerSelf from '../../../BuiltInComponent/DatePricker';
import TextFields from '../../../BuiltInComponent/TextField';
import HeadingText from '../../../BuiltInComponent/HeadingTest';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { getSupplierList } from '../../../features/setup/useableSlice';
import { createPIDetail, createPIMaster, getDraftGRNs, getGRNDetails, getPIData } from '../../../features/StockManagement/Purchases/piSlice';
import { createPurchaseIdempotencyKey } from '../../../utils/purchaseIdempotencyKey';
import ManualPurchaseItemPicker from './ManualPurchaseItemPicker';
import usePurchaseWorkflow from '../../../hooks/usePurchaseWorkflow';
import { getItemList } from '../../../features/setup/useableSlice';

export default function AddPurchaseInvoice() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useSelector((state) => state.auth);
    const { suppliers, items } = useSelector((state) => state.ru);
    const { startStage } = usePurchaseWorkflow();
    const { draftGRNs } = useSelector((state) => state.pi);
    const lock = React.useRef(false);
    const masterKey = React.useRef(null);
    const rateInputRefs = React.useRef([]);
    const [saving, setSaving] = React.useState(false);
    const [selectedPDID, setSelectedPDID] = React.useState(null);
    const [details, setDetails] = React.useState([]);
    const [master, setMaster] = React.useState({
        entDate: dayjs().format('YYYY-MM-DD'), piID: '', userID: user?.userID || 0,
        PartyCode: '', remarks: '', piStatus: 'Pending', taxEnabled: false, taxAuthority: 'FBR', taxRate: 0, editStatus: false
    });

    React.useEffect(() => {
        dispatch(getSupplierList());
        dispatch(getItemList('purchase'));
        dispatch(getDraftGRNs());
        if (location.state?.piID) {
            dispatch(getPIData(location.state.piID)).unwrap().then((data) => {
                const row = data?.pm?.[0];
                if (!row) return;
                setMaster({ ...row, editStatus: true });
                setDetails(data?.pi || []);
            }).catch((error) => alert(`Invoice load failed: ${error}`));
        }
    }, [dispatch, location.state?.piID]);

    const selectGRN = async (data) => {
        if (!data?.pdID || details.some((row) => Number(row.pdID) === Number(data.pdID))) return;
        try {
            const rows = await dispatch(getGRNDetails(data.pdID)).unwrap();
            setSelectedPDID(data.pdID);
            setMaster((prev) => ({ ...prev, PartyCode: data.PartyCode || rows?.[0]?.PartyCode || prev.PartyCode }));
            setDetails((prev) => [...prev, ...(rows || []).map((row) => ({ ...row, pdID: data.pdID }))]);
        } catch (error) { alert(`GRN details load failed: ${error}`); }
    };

    const saveMaster = async () => {
        if (lock.current) return;
        lock.current = true; setSaving(true);
        try {
            const isNew = !master.piID;
            if (isNew && !masterKey.current) masterKey.current = createPurchaseIdempotencyKey('PI', user?.userID);
            const result = await dispatch(createPIMaster({ ...master, idempotencyKey: isNew ? masterKey.current : null })).unwrap();
            setMaster((prev) => ({ ...prev, piID: result.piID }));
            if (isNew) masterKey.current = null;
        } catch (error) { alert(`Purchase Invoice save failed: ${error}`); }
        finally { setSaving(false); lock.current = false; }
    };

    const saveDetails = async () => {
        if (lock.current) return;
        lock.current = true; setSaving(true);
        try {
            await dispatch(createPIDetail({ piID: master.piID, detailData: details, editStatus: master.editStatus })).unwrap();
            navigate('/PurchaseInvoice');
        } catch (error) { alert(`Purchase Invoice details save failed: ${error}`); }
        finally { setSaving(false); lock.current = false; }
    };

    const handleRateKeyDown = (event, index) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        const targetIndex = event.key === 'ArrowDown' ? index + 1 : index - 1;
        const targetInput = rateInputRefs.current[targetIndex];
        if (!targetInput) return;
        event.preventDefault();
        targetInput.focus();
        targetInput.select();
    };

    const subtotal = details.reduce((sum, row) => sum + (Number(row.quantity || 0) * Number(row.itemRate || 0)), 0);
    const taxRate = master.taxEnabled ? Number(master.taxRate || 0) : 0;
    const taxAmount = subtotal * taxRate / 100;
    const grandTotal = subtotal + taxAmount;

    return <><Navbar /><Container><Grid2 container spacing={2} sx={{ mt: 3 }}>
        <Grid2 size={12}><HeadingText text='Purchase Invoice' /></Grid2>
        <Grid2 size={0.5}>
            <KeyboardBackspaceIcon
                sx={{
                    fontSize: 30, marginLeft: -1, color: 'white', cursor: 'pointer',
                    backgroundColor: '#1D4988', padding: 1, borderRadius: 50
                }}
                onClick={() => navigate('/PurchaseInvoice')} />
        </Grid2>
        <Grid2 size={11.5}></Grid2>
        <Grid2 size={2}><DatePickerSelf name='entDate' value={master.entDate} onChange={(data) => setMaster((p) => ({ ...p, ...data }))} /></Grid2>
        <Grid2 size={3}><AutoComplete label='Select Supplier' listData={suppliers || []}
            value={(suppliers || []).find((p) => p.PartyCode === master.PartyCode)} onChange={(data) => setMaster((p) => ({ ...p, ...data }))}
            disabledValue={Boolean(master.piID)} /></Grid2>
        <Grid2 size={2}><MuiTextField select fullWidth size='small' label='Tax Type' value={master.taxEnabled ? 'yes' : 'no'} onChange={(e) => setMaster((p) => ({ ...p, taxEnabled: e.target.value === 'yes' }))}><MenuItem value='no'>No Tax</MenuItem><MenuItem value='yes'>GST / SST</MenuItem></MuiTextField></Grid2>
        {master.taxEnabled && <><Grid2 size={1.5}><MuiTextField select fullWidth size='small' label='Authority' value={master.taxAuthority || 'FBR'} onChange={(e) => setMaster((p) => ({ ...p, taxAuthority: e.target.value }))}><MenuItem value='FBR'>FBR / GST</MenuItem><MenuItem value='SRB'>SRB / SST</MenuItem></MuiTextField></Grid2><Grid2 size={1.5}><TextFields label='Tax Rate %' name='taxRate' value={master.taxRate ?? 0} onChange={(data) => setMaster((p) => ({ ...p, ...data }))} disStatus={false} /></Grid2></>}
        <Grid2 size={master.taxEnabled ? 2 : 5}><TextFields label='Remarks' name='remarks' value={master.remarks || ''} onChange={(data) => setMaster((p) => ({ ...p, ...data }))} disStatus={false} /></Grid2>
        <Grid2 size={2}><ButtonNormal text='Save Invoice Master' onClick={saveMaster} loading={saving} disabled={saving} /></Grid2>
        {master.piID && <>
            <Grid2 size={12}>{startStage === 'PI' ? <ManualPurchaseItemPicker items={items} stage="PI" onAdd={(row) => setDetails((prev) => [...prev, row])} /> : <AutoComplete label='Select GRN' listData={draftGRNs || []}
                value={(draftGRNs || []).find((row) => row.pdID === selectedPDID)} onChange={selectGRN} />}</Grid2>
            <Grid2 size={12}><TableContainer component={Paper}><Table size='small'><TableHead><TableRow>
                <TableCell>Item</TableCell><TableCell>Quantity</TableCell><TableCell>Rate</TableCell><TableCell>Amount</TableCell><TableCell align='center'>Delete</TableCell>
            </TableRow></TableHead><TableBody>{details.map((row, index) => <TableRow key={`${row.pdID}-${row.itemCode}-${index}`}>
                <TableCell>{row.itemDesc}</TableCell><TableCell><TextFields name='quantity' value={row.quantity || ''}
                    onChange={(data) => setDetails((current) => current.map((item, i) => i === index ? { ...item, quantity: data.quantity } : item))} disStatus={false} /></TableCell>
                <TableCell><TextFields name='itemRate' value={row.itemRate || ''}
                    inputRef={(element) => { rateInputRefs.current[index] = element; }}
                    onKeyDown={(event) => handleRateKeyDown(event, index)}
                    onChange={(data) => setDetails((current) => current.map((item, i) => i === index ? { ...item, itemRate: data.itemRate } : item))} disStatus={false} /></TableCell>
                <TableCell>{(Number(row.quantity || 0) * Number(row.itemRate || 0)).toFixed(2)}</TableCell>
                <TableCell align='center'><Tooltip title='Delete row'><IconButton color='error' size='small'
                    onClick={() => setDetails((current) => current.filter((_, i) => i !== index))}>
                    <DeleteForeverIcon />
                </IconButton></Tooltip></TableCell>
            </TableRow>)}</TableBody></Table></TableContainer></Grid2>
            <Grid2 size={12}><Paper variant='outlined' sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 4, flexWrap: 'wrap' }}><Typography><b>Subtotal:</b> Rs. {subtotal.toFixed(2)}</Typography>{master.taxEnabled && <Typography><b>{master.taxAuthority === 'SRB' ? 'SST' : 'GST'} ({taxRate}%):</b> Rs. {taxAmount.toFixed(2)}</Typography>}<Typography><b>Grand Total:</b> Rs. {grandTotal.toFixed(2)}</Typography></Paper></Grid2>
            <Grid2 size={2}><ButtonNormal text='Save Invoice Details' onClick={saveDetails} loading={saving} disabled={saving || !details.length} /></Grid2>
        </>}
        {!master.piID && <Grid2 size={12}><Typography color='text.secondary'>Save invoice master before adding GRN items.</Typography></Grid2>}
    </Grid2></Container></>;
}
